import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, type SupportedLanguage, isRTL } from '../i18n';
import { LanguageContext, type LanguageContextType } from './languageContextDef';

// Re-export types and context
export { LanguageContext, type LanguageContextType } from './languageContextDef';

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n, t } = useTranslation();
  
  // i18n.language may include a region suffix (e.g. "he-IL") on some platforms
  // even with `load: 'languageOnly'`.  Strip it so the value always matches
  // the SupportedLanguage union ('he' | 'en').
  const rawLang = i18n.language || 'he';
  const language = (rawLang.split('-')[0] || 'he') as SupportedLanguage;
  const currentIsRTL = isRTL(language);
  const direction = currentIsRTL ? 'rtl' : 'ltr';

  const changeLanguage = useCallback((lang: SupportedLanguage) => {
    i18n.changeLanguage(lang);
  }, [i18n]);

  const value: LanguageContextType = {
    language,
    changeLanguage,
    isRTL: currentIsRTL,
    direction,
    supportedLanguages,
    t: t as (key: string, options?: Record<string, unknown>) => string,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
